/**
 * The column's `type` is the platform's real, existing Formly field type
 * (not a custom abstraction) — the "Field type" dropdown in the config
 * wizard maps onto one of these, matching how every other field type
 * already registers with the form engine.
 */
export type TableWithInputColumnFieldType =
  | 'input'
  | 'customdatepicker'
  | 'customdropdown'
  | 'checkbox';

export interface TableWithInputColumnProps {
  label: string;
  type?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  // Inverted polarity vs. the wizard's "Show field in table" toggle
  // (Yes/No): a column is shown in the table by default, so this defaults
  // to false/absent, only becoming true when the SM explicitly hides it.
  hiddenColumn?: boolean;
  widthPercent?: number | null;
  unique?: boolean;
  // Extra keys beyond label/value are allowed so bindLabel/bindValue can
  // point at differently-named properties, matching custom-drop-down's
  // options shape.
  options?: ({ label: string; value: any } & Record<string, unknown>)[];
  multiple?: boolean;
  bindValue?: string;
  bindLabel?: string;
  default?: boolean;
  pattern?: string;
  hideLabel?: boolean;
  description?: string;
  indeterminate?: boolean;
  // Date columns forward the rest of their props as-is to
  // irembogov-irembo-date-picker (maxDate, minDate, dateFormat, delimeter,
  // eventCode, eventDatesMaxSize, addRemoveTimeMinDate, addRemoveTimeMaxDate,
  // allowedDaysOfTheWeek, excludedDates), matching what the existing
  // customdatepicker Formly wrapper already supports — loosened to `any`
  // (from `unknown`) so those bindings type-check the same way that
  // wrapper's own `props: Record<string, any>` does.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface TableWithInputColumn {
  key: string;
  type: TableWithInputColumnFieldType;
  className?: string;
  props: TableWithInputColumnProps;
  expressions?: Record<string, unknown>;
  validation?: {
    messages?: {
      required?: string;
      unique?: string;
      [key: string]: string | undefined;
    };
  };
}

export type TableWithInputRow = Record<string, unknown> & { __rowId: string };

export type TableWithInputColumnKind =
  | 'text'
  | 'number'
  | 'email'
  | 'date'
  | 'select'
  | 'checkbox';

// Shared by the live field (custom-table-with-input) and the read-only
// application summary/review preview (application-form.util) so both agree
// on how a column's stored value should be interpreted and displayed.
export const resolveTableWithInputColumnKind = (
  column: TableWithInputColumn
): TableWithInputColumnKind => {
  if (column.type === 'customdatepicker') {
    return 'date';
  }
  if (column.type === 'customdropdown') {
    return 'select';
  }
  if (column.type === 'checkbox') {
    return 'checkbox';
  }
  if (column.props?.type === 'number') {
    return 'number';
  }
  if (column.props?.type === 'email') {
    return 'email';
  }
  return 'text';
};

// Column options honor a custom bindValue/bindLabel (matching
// custom-drop-down's ng-select convention) instead of always assuming the
// option shape is exactly {label, value} — shared so the live field and the
// read-only preview builder resolve an option's value/label identically.
export const getTableWithInputOptionValue = (
  column: TableWithInputColumn,
  option: Record<string, unknown>
): unknown => option[column.props?.bindValue ?? 'value'];

const stringifyCellValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

export const getTableWithInputOptionLabel = (
  column: TableWithInputColumn,
  option: Record<string, unknown>
): string => stringifyCellValue(option[column.props?.bindLabel ?? 'label']);

// Resolves a stored cell value (or, for a `multiple` column, each value in
// the stored array) to its configured option's label, honoring
// bindValue/bindLabel. Falls back to the raw value when no matching option
// is found so unexpected/legacy data doesn't just disappear.
export const resolveTableWithInputSelectDisplayValues = (
  column: TableWithInputColumn,
  value: unknown
): string[] => {
  const options = column.props?.options ?? [];
  let rawValues: unknown[];
  if (column.props?.multiple) {
    rawValues = Array.isArray(value) ? value : [];
  } else {
    rawValues = [value];
  }

  return rawValues.map(rawValue => {
    const option = options.find(
      opt => getTableWithInputOptionValue(column, opt) === rawValue
    );
    return option
      ? getTableWithInputOptionLabel(column, option)
      : stringifyCellValue(rawValue);
  });
};
